/**
 * DevFixtureProvider — seeds the React Query cache with fixture data for local
 * development, toggled via the `?mock=` URL search param.
 *
 * Usage:
 *   http://localhost:3000/jobs/mock-dev-job/coverage-qa?mock=coverageQa
 *
 * The provider:
 *  1. Reads `?mock=<setName>` from the URL
 *  2. Looks up a FixtureSet from `fixture-sets.ts`
 *  3. Seeds the QueryClient cache so hooks find data already present
 *  4. Exposes mutation overrides (e.g. imageGallery) via React context
 *  5. Renders a yellow dev banner at the bottom of the viewport
 */
import { createContext, useContext, useRef, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { fixtureSets, MOCK_JOB_ID, type FixtureSet } from "./fixture-sets";
import { useCoverageBetaStore } from "@/store/useCoverageBetaStore";
import {
  COVERAGE_BETA_SUBMIT_ENDPOINT,
  buildCoverageBetaResult,
} from "@/utils/build-coverage-beta-result";
import { CompleteTaskPayloadSchema } from "@/utils/zod-schemas/complete-task-payload-schema";

// ---------------------------------------------------------------------------
// Context for mutation overrides
// ---------------------------------------------------------------------------

interface DevFixtureContextValue {
  active: boolean;
  setName: string | null;
  mutationOverrides: FixtureSet["mutationOverrides"];
}

const DevFixtureContext = createContext<DevFixtureContextValue>({
  active: false,
  setName: null,
  mutationOverrides: undefined,
});

export const useDevFixtures = () => useContext(DevFixtureContext);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function DevFixtureProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const seededRef = useRef<string | null>(null);

  const mockParam = searchParams.get("mock");

  // Resolve the fixture set (supports "setName" and "setName:sub")
  const fixtureSet = mockParam ? fixtureSets[mockParam]?.() : null;

  // Seed the cache synchronously during render (before children mount) so
  // that child hooks find data already cached and don't fire real API calls.
  if (fixtureSet && seededRef.current !== mockParam) {
    seededRef.current = mockParam;

    for (const { queryKey, data } of fixtureSet.querySeeds) {
      queryClient.setQueryData(queryKey, data);
    }

    console.log(
      `[DevFixture] Seeded cache with "${mockParam}" (${fixtureSet.querySeeds.length} queries)`,
    );
  }

  // Expose helpers on window for live console editing
  if (fixtureSet) {
    exposeDevHelpers(queryClient);
  }

  // `dev.triggerSubmit()` — always available in dev, mock or not.
  exposeCoverageBetaTrigger(queryClient);

  const contextValue: DevFixtureContextValue = {
    active: !!fixtureSet,
    setName: mockParam,
    mutationOverrides: fixtureSet?.mutationOverrides,
  };

  return (
    <DevFixtureContext.Provider value={contextValue}>
      {children}
      {fixtureSet && <DevBanner setName={mockParam!} />}
    </DevFixtureContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Dev Banner
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Console helpers — available as window.dev in mock mode
// ---------------------------------------------------------------------------

interface DevHelpers {
  /** The React Query client — use setQueryData / getQueryData directly */
  queryClient: QueryClient;
  /** Shorthand to read current cached data for a query key */
  get: (key: string) => unknown;
  /** Shorthand to update cached data — merges partial data and triggers re-render */
  set: (key: string, data: unknown) => void;
  /** Print all seeded query keys */
  keys: () => void;
}

const keyMap: Record<string, unknown[]> = {
  job: ["job", MOCK_JOB_ID],
  task: ["nextPendingTask", MOCK_JOB_ID],
  parcel: ["parcel", "parcel-001"],
  planogram: ["fetch-atlas-planogram", "plano-001"],
};

function exposeDevHelpers(queryClient: QueryClient) {
  const helpers: DevHelpers = {
    queryClient,
    get: (key) => {
      const qk = keyMap[key];
      if (!qk) {
        console.warn(
          `[dev.get] Unknown key "${key}". Use dev.keys() to see available keys.`,
        );
        return undefined;
      }
      return queryClient.getQueryData(qk);
    },
    set: (key, data) => {
      const qk = keyMap[key];
      if (!qk) {
        console.warn(
          `[dev.set] Unknown key "${key}". Use dev.keys() to see available keys.`,
        );
        return;
      }
      queryClient.setQueryData(qk, data);
      console.log(`[dev.set] Updated "${key}" — UI should re-render.`);
    },
    keys: () => {
      console.table(
        Object.entries(keyMap).map(([alias, qk]) => ({
          alias,
          queryKey: JSON.stringify(qk),
          hasData: queryClient.getQueryData(qk) !== undefined,
        })),
      );
    },
  };
  (window as unknown as Record<string, unknown>).dev = helpers;
}

/**
 * Exposes `window.dev.triggerSubmit()` — builds the coverage-beta completion
 * payload from current store state, logs the endpoint + body + schema-validation
 * result to the console, and stashes the body at `window.__coverageBetaPayload`.
 * Dry run: it does NOT send the request. For validating the payload shape.
 */
function exposeCoverageBetaTrigger(queryClient: QueryClient) {
  const w = window as unknown as Record<string, unknown>;
  const dev = (w.dev as Record<string, unknown>) ?? {};
  w.dev = dev;

  dev.triggerSubmit = () => {
    const jobId =
      window.location.pathname.match(/\/jobs\/([^/]+)/)?.[1] ?? "<job_id>";

    // Mirror the real submit: `testing` comes from the cached job (["job", id]).
    const job = queryClient.getQueryData<{ testing?: boolean }>(["job", jobId]);

    const {
      idxToFeEmptyArea,
      persons,
      basePlanogramIds,
      overridePlanogramIds,
      extraPlanogramIds,
      boxIdxToSlot,
      primaryImageId,
    } = useCoverageBetaStore.getState();
    const result = buildCoverageBetaResult(
      idxToFeEmptyArea,
      persons,
      {
        basePlanogramIds,
        overridePlanogramIds,
        extraPlanogramIds,
        boxIdxToSlot,
      },
      { testing: job?.testing ?? false, primaryImageId },
    );
    const body = { result };

    const { method, urlTemplate } = COVERAGE_BETA_SUBMIT_ENDPOINT;
    const url = urlTemplate
      .replace("{job_id}", jobId)
      .replace("{task_id}", "<task_id>");

    const parsed = CompleteTaskPayloadSchema.safeParse(body);

    console.group(
      "%c[dev.triggerSubmit] coverage-beta payload (dry run — not sent)",
      "color:#7c3aed;font-weight:600",
    );
    console.log(`${method} ${url}`);
    console.log("body:", body);
    console.log("body (json):", JSON.stringify(body, null, 2));
    if (parsed.success) {
      console.log(
        "%c✓ payload valid (CompleteTaskPayloadSchema)",
        "color:#16a34a;font-weight:600",
      );
    } else {
      console.warn("✗ payload INVALID:", parsed.error.issues);
    }
    console.groupEnd();

    w.__coverageBetaPayload = body;
    return body;
  };
}

function DevBanner({ setName }: { setName: string }) {
  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        background: "#fbbf24",
        color: "#78350f",
        textAlign: "center",
        fontSize: 12,
        fontWeight: 600,
        padding: "4px 0",
        zIndex: 9999,
      }}
    >
      DEV MOCK ACTIVE: {setName} — remove ?mock= param to use real API
    </div>
  );
}
